import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import SimpleDataTable, { type TableColumn } from "./SimpleDataTable";
import { TodoTypes, type TodoItem } from "~/api/v2/useMyTodos";
import { dateDiff } from "~/helpers/date";
import {
  getTodoTypeText,
  getTodoIcon,
  getIconColor,
  getTodoDate,
  compareTodoOrder,
  getTodoNavigationPath,
} from "~/helpers/todo";
import { DueDateCell } from "~/molecules/DueDateCell";
import { useUserContext } from "~/hooks/useUserContext";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import { formatDate } from "~/helpers/date";

interface Props {
  todos: TodoItem[];
  onClickRow?: (item: TodoItem) => void;
}

const TodoDataTable = ({ todos, onClickRow }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUserContext();

  const columns: TableColumn<TodoItem>[] = [
    {
      header: t(($) => $.dashboard.column.todo),
      defaultSort: true,
      compareFn: compareTodoOrder,
      cell: (todo) => {
        const statusText = getTodoTypeText(todo, user.id);
        const Icon = getTodoIcon(todo);
        const iconColor = getIconColor(todo);

        return (
          <div className="flex min-w-0 items-center gap-2">
            {Icon && <Icon className={`size-4 shrink-0 ${iconColor}`} />}
            <p className="truncate" title={statusText}>
              {statusText}
            </p>
          </div>
        );
      },
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
    {
      header: t(($) => $.dashboard.column.title),
      compareFn: (a, b) => a.title.localeCompare(b.title),
      cell: (item) => (
        <div className="min-w-0">
          <span className="block truncate" title={item.title}>
            {item.title}
          </span>
        </div>
      ),
      cellClassName: "w-full max-w-0",
      tableHeadClassName: "w-full",
    },
    {
      header: t(($) => $.dashboard.myTodos.dateLabels.date),
      compareFn: (a, b) => {
        return dateDiff(getTodoDate(a), getTodoDate(b));
      },
      cell: (todo: TodoItem) => {
        const dueDate = getTodoDate(todo);
        const isToRead = todo.todoType === TodoTypes.documentToRead;

        return dueDate ? (
          <DueDateCell
            renderedDate={formatDate(dueDate)}
            dueDate={dueDate}
            className={isToRead ? "text-foreground" : undefined}
            showTriangleAlert={!isToRead}
          />
        ) : null;
      },
      cellClassName: "min-w-40 whitespace-nowrap",
      tableHeadClassName: "min-w-40 whitespace-nowrap",
    },
    {
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) => a.owner.firstName.localeCompare(b.owner.firstName),
      cell: (item) => (
        <AvatarWithFallback user={item.owner} size="xs" showTooltip />
      ),
      cellClassName: "whitespace-nowrap",
      tableHeadClassName: "whitespace-nowrap",
    },
  ];

  return (
    <SimpleDataTable
      columns={columns}
      data={todos}
      onClickRow={(item) => {
        void navigate(getTodoNavigationPath(item));
        onClickRow?.(item);
      }}
      wrapperClassName="max-h-95"
    />
  );
};

export default TodoDataTable;
